import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { Form } from 'antd';

import { DASHBOARD_ITEM_API_NAMES, SCHEDULE_TYPES } from '@src/constants/commons';
import useSelectGroupsWithPagination from '@src/packages/utils/hooks/collections/useSelectGroupsWithPagination';
import useGetEventTypes from '@src/packages/utils/hooks/useGetEventTypes';
import CounterChartService from '@src/services/CounterChartService';

import { FILTER_DATA_OPTIONS } from '../../../components/Shared/constants';
import {
  ALL_TEMPLATES,
  DASHBOARDS_EVENT_TYPE,
  LAYOUT_BY_COMPONENT_TYPE,
  USERS_TYPE,
} from '../../../constants';

const PAGE_SIZE = 20;

const getLocationCombinerValue = queryParams => {
  const { locationIds, locationGroupId } = queryParams || {};
  if (locationGroupId) {
    return { isAGroup: true, data: { id: locationGroupId } };
  }
  if (locationIds?.length) {
    return { isAGroup: false, data: locationIds.map(id => ({ id })) };
  }
  return null;
};

const useCounterNumberComponent = ({ divId, boardId, chartId }) => {
  const [form] = Form.useForm();
  const counterChartService = useMemo(() => new CounterChartService(), []);
  const orgId = useSelector(({ organizations }) => organizations?.organization?.id);
  const userOrgDivisions = useSelector(({ divisions }) => divisions?.assigned?.data?.divisions);
  const [type, setType] = useState(null);
  const [selectedSchedules, setSelectedSchedules] = useState({ byId: {}, selectedAll: false });
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [initialScheduleIds, setInitialScheduleIds] = useState([]);
  const [fetchedChartData, setFetchedChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  const watchUsersType = Form.useWatch('usersType', form);
  const watchDivisionIds = Form.useWatch('divisionId', form);
  const watchFormId = Form.useWatch('formId', form);

  const {
    data: groupOptions,
    loading: groupsLoading,
    handlePopupScroll: handlePopupScrollGroups,
  } = useSelectGroupsWithPagination({
    orgId,
    pageSize: PAGE_SIZE,
    divIds: divId ? [divId] : null,
    orderByConfig: { field: 'name', direction: 'asc' },
  });
  const { data: eventTypes, loading: eventTypesLoading } = useGetEventTypes(true, watchDivisionIds);

  const eventTypesWithNumComponents = useMemo(() => {
    const output = eventTypes;
    const event = output.find(event => event?.id === watchFormId);

    if (!event) return [];

    return event?.components
      ?.filter(({ type }) => type === 'number')
      .map(component => ({ label: component.label.name, value: component.id }));
  }, [eventTypes, watchFormId]);

  const mainLoading = eventTypesLoading || loading;
  const filterDataOptions = useMemo(() => {
    return [...FILTER_DATA_OPTIONS].filter(
      option => option.value !== SCHEDULE_TYPES.checkpoint_route,
    );
  }, []);

  const buildBody = useCallback(() => {
    const {
      title,
      description,
      usersType,
      userIds,
      groupIds,
      formId,
      triggerId,
      componentId,
      schedulesIds,
      divisionId,
      templateIds,
      type,
      layout,
      locationIds: location,
      unit,
    } = form.getFieldsValue();

    const usersOrGroups = {};
    if (usersType === USERS_TYPE.ALL) {
      usersOrGroups.userIds = [];
      usersOrGroups.groupIds = [];
    } else if (usersType === USERS_TYPE.USERS) {
      usersOrGroups.userIds = userIds;
      usersOrGroups.groupIds = [];
    } else if (usersType === USERS_TYPE.GROUPS) {
      usersOrGroups.userIds = [];
      usersOrGroups.groupIds = groupIds;
    }

    const locationIdsOrGroup = {};
    if (location?.isAGroup === true && location?.data?.id) {
      locationIdsOrGroup.locationGroupId = location.data.id;
    } else if (location?.isAGroup === false && location?.data?.length) {
      locationIdsOrGroup.locationIds = location.data.map(loc => loc.id);
    }

    const body = {
      title: title ?? '',
      description: description ?? '',
      apiName: DASHBOARD_ITEM_API_NAMES.EVENT_COUNTER_NUMBER_COMPONENT,
      layout: layout || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.COUNTER_CHARTS],
      queryParams: {
        ...usersOrGroups,
        formId: formId ?? null,
        triggerId: triggerId ?? null,
        componentId: componentId ?? null,
        scheduleIds: selectedSchedules.selectedAll ? [] : schedulesIds,
        templateIds: templateIds?.includes(ALL_TEMPLATES) ? [] : templateIds,
        divisionIds: divisionId ? [divisionId] : [],
        type: type ?? null,
        unit: unit ?? null,
        ...locationIdsOrGroup,
      },
    };

    form.setFieldValue('formData', body);
    setType(type ?? null);
  }, [form, selectedSchedules.selectedAll]);

  const handleClear = useCallback(() => {
    setType(null);
    setSelectedSchedules({ byId: {}, selectedAll: false });
    form.resetFields();
  }, [form]);

  const handleTypeChange = useCallback(() => {
    form.setFieldsValue({ schedulesIds: [], templateIds: [] });
    setSelectedSchedules({ byId: {}, selectedAll: true });
    // buildBody for update the body with the new type
    buildBody();
  }, [form, buildBody]);

  const handleDivisionChange = useCallback(
    e => {
      form.setFieldsValue({
        componentId: null,
        formId: null,
        triggerId: null,
        locationIds: null,
      });
      // buildBody for update the body with the new type
      buildBody();
    },
    [form, buildBody],
  );

  const handleTriggerChange = useCallback(
    trigger => {
      form.setFieldValue('triggerId', trigger?.id ?? null);
      buildBody();
    },
    [form, buildBody],
  );

  const handleFormChange = useCallback(() => {
    form.setFieldValue('triggerId', null);
    form.setFieldValue('componentId', null);
    buildBody();
  }, [form, buildBody]);

  const resetUsersAndGroups = useCallback(() => {
    form.setFieldValue('userIds', []);
    form.setFieldValue('groupIds', []);
  }, [form]);

  const divisions = useMemo(
    () =>
      Object.values(userOrgDivisions || {})
        .map(division => ({
          name: division.name,
          value: division.id,
        }))
        .filter(division => (divId ? division.value === divId : true)),
    [userOrgDivisions, divId],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!chartId || !boardId || !orgId) return;

      setLoading(true);
      try {
        const res = await counterChartService.getCounterChart({ orgId, divId, boardId, chartId });
        if (res) {
          setFetchedChartData(res);
          const {
            title: titleFetched,
            description: descriptionFetched,
            queryParams: queryParamsFetched = {},
            layout: layoutFetched,
          } = res;

          const {
            userIds: userIdsFetched = [],
            groupIds: groupIdsFetched = [],
            formId: formIdFetched = null,
            componentId: componentIdFetched = null,
            scheduleIds: scheduleIdsFetched = [],
            templateIds: templateIdsFetched = [],
            divisionIds: divisionIdsFetched = [],
            triggerId: triggerIdFetched = null,
            locationIds: locationIdsFetched,
            locationGroupId: locationGroupIdFetched,
            type: typeFetched = null,
            unit = null,
          } = queryParamsFetched;

          let usersTypeFetched = USERS_TYPE.ALL;
          if (userIdsFetched && userIdsFetched.length > 0) {
            usersTypeFetched = USERS_TYPE.USERS;
          } else if (groupIdsFetched && groupIdsFetched.length > 0) {
            usersTypeFetched = USERS_TYPE.GROUPS;
          }

          form.setFieldsValue({
            id: chartId,
            title: titleFetched || '',
            description: descriptionFetched || '',
            userIds: userIdsFetched || [],
            groupIds: groupIdsFetched || [],
            formId: formIdFetched || null,
            componentId: componentIdFetched || null,
            scheduleIds: scheduleIdsFetched || [],
            templateIds: templateIdsFetched || [],
            divisionId: divisionIdsFetched[0] || [],
            triggerId: triggerIdFetched || null,
            locationIds: getLocationCombinerValue({
              locationIds: locationIdsFetched,
              locationGroupId: locationGroupIdFetched,
            }),
            type: typeFetched || null,
            layout: layoutFetched || LAYOUT_BY_COMPONENT_TYPE[DASHBOARDS_EVENT_TYPE.COUNTER_CHARTS],
            usersType: usersTypeFetched,
            unit: unit || null,
          });

          setType(typeFetched || null);
        }
      } catch (error) {
        console.error('Error loading counter number component:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [boardId, chartId, counterChartService, divId, form, orgId]);

  return {
    form,
    buildBody,
    handleClear,
    divisions,
    eventTypes,
    eventTypesWithNumComponents,
    mainLoading,
    eventTypesLoading,
    loading,
    orgId,
    type,
    selectedSchedules,
    setSelectedSchedules,
    loadingSchedules,
    setLoadingSchedules,
    initialScheduleIds,
    setInitialScheduleIds,
    watchDivisionIds,
    fetchedChartData,
    handleTypeChange,
    handleDivisionChange,
    groupOptions,
    groupsLoading,
    handlePopupScrollGroups,
    watchUsersType,
    resetUsersAndGroups,
    filterDataOptions,
    handleTriggerChange,
    handleFormChange,
  };
};

export default useCounterNumberComponent;
